import { useEffect, useLayoutEffect, useState } from 'react';

const IS_SERVER = typeof window === 'undefined';

const useIsomorphicLayoutEffect = IS_SERVER ? useEffect : useLayoutEffect;

/**
 * A React hook that provides a way to check if the current browser window matches a given media query.
 *
 * @param query - The media query to check against.
 * @param defaultValue - The default value to return if the media query cannot be evaluated (e.g. on the server).
 * @returns A boolean indicating whether the current browser window matches the given media query.
 */
export function useMediaQuery(query: string, defaultValue = false) {
  // Always start with `defaultValue` so the first client render matches SSR.
  // The real value is read in the effect below to avoid hydration mismatches.
  const [matches, setMatches] = useState<boolean>(defaultValue);

  function handleChange() {
    setMatches(IS_SERVER ? defaultValue : window.matchMedia(query).matches);
  }

  useIsomorphicLayoutEffect(() => {
    const matchMedia = window.matchMedia(query);
    handleChange();

    // Use deprecated `addListener` and `removeListener` to support Safari < 14 (#135)
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener('change', handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener('change', handleChange);
      }
    };
  }, [query]);

  return matches;
}
